'use client';

import {
  AZColoredIcon,
  AZIcon,
  CasinoColoredIcon,
  CasinoIcon,
  SearchColoredIcon,
  SearchIcon,
  SportsColoredIcon,
  SportsIcon,
  SupportColoredIcon,
  SupportIcon,
} from '@repo/icons';
import { cn } from '@repo/utils/cn';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useMemo } from 'react';
import BottomMenuItem from '@/components/layout/bottom-nav-bar/bottom-menu/bottom-menu-item';
import { useRouter } from '@/i18n/routing';
import { useAppStore } from '@/providers/AppStoreProvider';
import { useTenant } from '@/providers/TenantProvider';

const gridColsMap: Record<number, string> = {
  4: 'grid-cols-4',
  5: 'grid-cols-5',
};

const useTenantIcons = (tenantMachineName: string) => {
  return useMemo(() => {
    const isColoredTenant = ['vatan-game', 'hazarat-game'].includes(tenantMachineName);
    return {
      menuIcon: isColoredTenant ? AZColoredIcon : AZIcon,
      sportsIcon: isColoredTenant ? SportsColoredIcon : SportsIcon,
      casinoIcon: isColoredTenant ? CasinoColoredIcon : CasinoIcon,
      searchIcon: isColoredTenant ? SearchColoredIcon : SearchIcon,
      supportIcon: isColoredTenant ? SupportColoredIcon : SupportIcon,
    };
  }, [tenantMachineName]);
};

const BottomMenu = memo(() => {
  const t = useTranslations('bottom-nav-bar');

  // Optimize state selectors to only subscribe to needed state
  const setIsMoreMenuItemsDialogOpen = useAppStore((state) => state.setIsMoreMenuItemsDialogOpen);
  const setIsSearchCasinoGamesDialogOpen = useAppStore((state) => state.setIsSearchCasinoGamesDialogOpen);
  const isMoreMenuItemsDialogOpen = useAppStore((state) => state.isMoreMenuItemsDialogOpen);
  const isSearchCasinoGamesDialogOpen = useAppStore((state) => state.isSearchCasinoGamesDialogOpen);
  const currentPathname = useAppStore((state) => state.currentPathname);

  const router = useRouter();
  const { tenantMachineName } = useTenant();
  const icons = useTenantIcons(tenantMachineName);

  // Memoize click handlers to prevent unnecessary re-renders
  const handleMenuClick = useCallback(() => {
    setIsMoreMenuItemsDialogOpen(!isMoreMenuItemsDialogOpen);
  }, [setIsMoreMenuItemsDialogOpen, isMoreMenuItemsDialogOpen]);

  const handleSportsClick = useCallback(() => {
    router.push('/sportsbook');
  }, [router]);

  const handleCasinoClick = useCallback(() => {
    router.push('/casino');
  }, [router]);

  const handleExploreClick = useCallback(() => {
    setIsSearchCasinoGamesDialogOpen(!isSearchCasinoGamesDialogOpen);
  }, [setIsSearchCasinoGamesDialogOpen, isSearchCasinoGamesDialogOpen]);

  const items = useMemo(
    () => [
      {
        key: 'menu',
        title: t('menu'),
        Icon: icons.menuIcon,
        onClick: handleMenuClick,
        isActive: isMoreMenuItemsDialogOpen,
      },
      {
        key: 'sports',
        title: t('sports'),
        Icon: icons.sportsIcon,
        onClick: handleSportsClick,
        isActive:
          currentPathname?.startsWith('/sportsbook') && !isSearchCasinoGamesDialogOpen && !isMoreMenuItemsDialogOpen,
      },
      {
        key: 'casino',
        title: t('casino'),
        Icon: icons.casinoIcon,
        onClick: handleCasinoClick,
        isActive:
          currentPathname?.startsWith('/casino') && !isSearchCasinoGamesDialogOpen && !isMoreMenuItemsDialogOpen,
      },
      {
        key: 'explore',
        title: t('explore'),
        Icon: icons.searchIcon,
        onClick: handleExploreClick,
        isActive: isSearchCasinoGamesDialogOpen,
      },
      {
        key: 'support',
        title: t('support'),
        Icon: icons.supportIcon,
        className: 'intercom_support',
        onClick: () => null,
      },
    ],
    [
      t,
      icons,
      handleMenuClick,
      handleSportsClick,
      handleCasinoClick,
      handleExploreClick,
      isMoreMenuItemsDialogOpen,
      isSearchCasinoGamesDialogOpen,
      currentPathname,
    ]
  );

  // Memoize grid class calculation
  const gridColsClass = useMemo(() => gridColsMap[items.length], [items.length]);

  return (
    <footer
      className={cn(
        'sticky inset-x-0 bottom-0 z-[var(--z-bottom-menu)] grid h-[var(--vt-bottom-nav-bar-height)] gap-x-2 overflow-hidden rounded-t-[10px] shadow-[-7px_-6px_2px_2px_#00000017] vt-bottom-menu-item-bg',
        gridColsClass,
        'sm:hidden'
      )}
      data-testid="bottom-menu-footer"
    >
      {items.map((item) => (
        <BottomMenuItem
          key={item.key}
          className={item.className}
          item={item}
          isActive={!!item.isActive}
          onClick={item.onClick}
        />
      ))}
    </footer>
  );
});

BottomMenu.displayName = 'BottomMenu';

export default BottomMenu;
